import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { apiClient as authenticatedApiClient } from './apiClient';
import { supabase } from './supabaseClient';
import type {
  Agent,
  Task,
  Crew,
  Tool,
  KnowledgeSource,
  CrewRun,
  CrewResult,
  CrewKickoffPrefillResponse,
  UsageLogEntry,
  GenerationRun,
  GenerationRunFilter,
  StartGenerationRunRequest,
  CompleteGenerationRunRequest,
  ContentCalendarKeyword,
  ContentCalendarFilters,
  BrandBible,
  GenerationTask,
  ContentCalendarAnalytics,
  KeywordStatus,
  ContentOptimizationRequest,
  ContentOptimizationResponse,
  OptimizationStatusResponse,
  ApplyOptimizationsRequest,
  ApplyOptimizationsResponse,
  ServiceImageListItem,
  ServiceImagesGetResponse,
  ServiceImage,
  ServiceImageDetail,
  ServiceImagesDeleteResponse,
  ServicePageCreationRequest,
  ServicePageCreationResponse,
} from '../types';
import type { TenantSettings, BuilderDetectionResult } from '../types/tenant';
import {
  TemplateConfig,
  SeoContentConfig,
  ContentReviewState,
  PublishRequest,
} from '../types/wizard';
import { DEFAULT_IMAGE_MODEL } from '../constants/imageModels';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor — attach the Supabase JWT so protected endpoints
    // (e.g. /api/plugin/disconnect, /api/plugin/save-key) authenticate. Was
    // previously a no-op (auth commented out), which 401'd every protected call
    // made through this client. Mirrors src/services/apiClient.ts.
    this.client.interceptors.request.use(
      async (config) => {
        if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
          config.headers.Authorization = 'Bearer dev-bypass-token';
        } else {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
              config.headers.Authorization = `Bearer ${session.access_token}`;
            }
          } catch {
            // Auth may be disabled — continue without token
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Agent endpoints
  async getAgents(): Promise<Agent[]> {
    const response = await this.client.get<Agent[]>('/agents');
    return response.data;
  }

  async getAgent(id: string): Promise<Agent> {
    const response = await this.client.get<Agent>(`/agents/${id}`);
    return response.data;
  }

  async createAgent(agent: Omit<Agent, 'id'>): Promise<Agent> {
    const response = await this.client.post<Agent>('/agents', agent);
    return response.data;
  }

  async updateAgent(id: string, agent: Partial<Agent>): Promise<Agent> {
    const response = await this.client.put<Agent>(`/agents/${id}`, agent);
    return response.data;
  }

  async deleteAgent(id: string): Promise<void> {
    await this.client.delete(`/agents/${id}`);
  }

  // Task endpoints
  async getTasks(): Promise<Task[]> {
    const response = await this.client.get<Task[]>('/tasks');
    return response.data;
  }

  async getTask(id: string): Promise<Task> {
    const response = await this.client.get<Task>(`/tasks/${id}`);
    return response.data;
  }

  async createTask(task: Omit<Task, 'id'>): Promise<Task> {
    const response = await this.client.post<Task>('/tasks', task);
    return response.data;
  }

  async updateTask(id: string, task: Partial<Task>): Promise<Task> {
    const response = await this.client.put<Task>(`/tasks/${id}`, task);
    return response.data;
  }

  async deleteTask(id: string): Promise<void> {
    await this.client.delete(`/tasks/${id}`);
  }

  // Crew endpoints
  async getCrews(): Promise<Crew[]> {
    const response = await this.client.get<Crew[]>('/crews');
    return response.data;
  }

  async getCrew(id: string): Promise<Crew> {
    const response = await this.client.get<Crew>(`/crews/${id}`);
    return response.data;
  }

  async createCrew(crew: Omit<Crew, 'id'>): Promise<Crew> {
    const response = await this.client.post<Crew>('/crews', crew);
    return response.data;
  }

  async updateCrew(id: string, crew: Partial<Crew>): Promise<Crew> {
    const response = await this.client.put<Crew>(`/crews/${id}`, crew);
    return response.data;
  }

  async deleteCrew(id: string): Promise<void> {
    await this.client.delete(`/crews/${id}`);
  }

  // Tool endpoints
  async getTools(): Promise<Tool[]> {
    const response = await this.client.get<Tool[]>('/tools');
    return response.data;
  }

  async getTool(id: string): Promise<Tool> {
    const response = await this.client.get<Tool>(`/tools/${id}`);
    return response.data;
  }

  async createTool(tool: Omit<Tool, 'id'>): Promise<Tool> {
    const response = await this.client.post<Tool>('/tools', tool);
    return response.data;
  }

  async updateTool(id: string, tool: Partial<Tool>): Promise<Tool> {
    const response = await this.client.put<Tool>(`/tools/${id}`, tool);
    return response.data;
  }

  async deleteTool(id: string): Promise<void> {
    await this.client.delete(`/tools/${id}`);
  }

  // Knowledge Source endpoints
  async getKnowledgeSources(): Promise<KnowledgeSource[]> {
    const response = await this.client.get<KnowledgeSource[]>('/knowledge-sources');
    return response.data;
  }

  async getKnowledgeSource(id: string): Promise<KnowledgeSource> {
    const response = await this.client.get<KnowledgeSource>(`/knowledge-sources/${id}`);
    return response.data;
  }

  async createKnowledgeSource(source: Omit<KnowledgeSource, 'id'>): Promise<KnowledgeSource> {
    const response = await this.client.post<KnowledgeSource>('/knowledge-sources', source);
    return response.data;
  }

  async updateKnowledgeSource(id: string, source: Partial<KnowledgeSource>): Promise<KnowledgeSource> {
    const response = await this.client.put<KnowledgeSource>(`/knowledge-sources/${id}`, source);
    return response.data;
  }

  async deleteKnowledgeSource(id: string): Promise<void> {
    await this.client.delete(`/knowledge-sources/${id}`);
  }

  // Usage & cost endpoints
  async getUsageLogs(tenantId?: string): Promise<UsageLogEntry[]> {
    const response = await this.client.get<UsageLogEntry[]>('/usage/logs', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return response.data;
  }

  async startGenerationRun(data: StartGenerationRunRequest): Promise<GenerationRun> {
    const response = await this.client.post<GenerationRun>('/usage/run/start', data);
    return response.data;
  }

  async getGenerationRuns(filter?: GenerationRunFilter): Promise<GenerationRun[]> {
    const params = filter
      ? Object.fromEntries(
          Object.entries(filter).filter(([, value]) => value !== undefined && value !== null && value !== 'all')
        )
      : undefined;
    const response = await this.client.get<GenerationRun[]>('/usage/runs', { params });
    return response.data;
  }

  async getGenerationRun(runId: string): Promise<GenerationRun> {
    const response = await this.client.get<GenerationRun>(`/usage/runs/${runId}`);
    return response.data;
  }

  async completeGenerationRun(runId: string, data: CompleteGenerationRunRequest): Promise<GenerationRun> {
    const response = await this.client.post<GenerationRun>(`/usage/run/${runId}/complete`, data);
    return response.data;
  }

  // Crew Run endpoints
  async getKickoffPrefill(crewId: string, website: string): Promise<CrewKickoffPrefillResponse> {
    const response = await this.client.get<CrewKickoffPrefillResponse>(
      `/crews/${crewId}/kickoff/prefill`,
      { params: { website } }
    );
    return response.data;
  }

  async uploadFile(file: File, website?: string): Promise<{ success: boolean; url: string; path: string; bucket: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (website) {
      formData.append('website', website);
    }

    const response = await this.client.post<{ success: boolean; url: string; path: string; bucket: string }>(
      '/tenant-settings/upload-file',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async kickoffCrew(
    crewId: string,
    inputs?: Record<string, any>,
    modelId?: string,
    imageModelId?: string,
  ): Promise<CrewRun> {
    const response = await this.client.post<CrewRun>(`/crews/${crewId}/kickoff`, {
      inputs,
      model_id: modelId,
      image_model_id: imageModelId,
    });
    return response.data;
  }

  async getCrewRuns(): Promise<CrewRun[]> {
    const response = await this.client.get<CrewRun[]>('/crew-runs');
    return response.data;
  }

  async getCrewRun(id: string): Promise<CrewRun> {
    const response = await this.client.get<CrewRun>(`/crew-runs/${id}`);
    return response.data;
  }

  async stopCrewRun(id: string): Promise<void> {
    await this.client.post(`/crew-runs/${id}/stop`);
  }

  async deleteWordpressPost(crewRunId: string, postId?: string | number): Promise<void> {
    await this.client.post(`/crew-runs/${crewRunId}/wordpress-post/delete`, {
      post_id: postId,
    });
  }

  // Results endpoints
  async getResults(): Promise<CrewResult[]> {
    const response = await this.client.get<CrewResult[]>('/results');
    return response.data;
  }

  async getResult(id: string): Promise<CrewResult> {
    const response = await this.client.get<CrewResult>(`/results/${id}`);
    return response.data;
  }

  async deleteResult(id: string): Promise<void> {
    await this.client.delete(`/results/${id}`);
  }

  // Tenant Settings - Image Upload
  async uploadImage(file: File, website?: string): Promise<{ success: boolean; url: string; path: string; bucket: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (website) {
      formData.append('website', website);
    }

    const response = await this.client.post<{ success: boolean; url: string; path: string; bucket: string }>(
      '/tenant-settings/upload-image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteImage(bucket: string, path: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete<{ success: boolean; message: string }>(
      '/tenant-settings/delete-image',
      {
        params: { bucket, path },
      }
    );
    return response.data;
  }

  // Tenant Settings - CRUD operations
  async getTenant(website: string): Promise<TenantSettings> {
    const response = await authenticatedApiClient.get<TenantSettings>(
      `/tenant-settings/${encodeURIComponent(website)}`
    );
    return response.data;
  }

  async createTenant(tenant: TenantSettings): Promise<TenantSettings> {
    const response = await authenticatedApiClient.post<TenantSettings>('/tenant-settings', tenant);
    return response.data;
  }

  async updateTenant(website: string, updates: Partial<TenantSettings>): Promise<TenantSettings> {
    const response = await authenticatedApiClient.patch<TenantSettings>(
      `/tenant-settings/${encodeURIComponent(website)}`,
      updates
    );
    return response.data;
  }

  async listTenants(): Promise<{website: string}[]> {
    const response = await authenticatedApiClient.get<{website: string}[]>('/tenant-settings/');
    return response.data;
  }

  async renameTenant(oldWebsite: string, newWebsite: string): Promise<{old_website: string, new_website: string}> {
    const response = await authenticatedApiClient.post<{old_website: string, new_website: string}>(
      `/tenant-settings/${encodeURIComponent(oldWebsite)}/rename`,
      { new_website: newWebsite }
    );
    return response.data;
  }

  // ====================================================
  // BUILDER DETECTION ENDPOINTS
  // ====================================================

  async detectBuilder(website: string): Promise<BuilderDetectionResult> {
    const response = await this.client.post<BuilderDetectionResult>(
      '/builder-detection/detect',
      { website }
    );
    return response.data;
  }

  async confirmBuilderConfig(
    website: string,
    config: { page_builder?: string; seo_plugin?: string; theme?: string }
  ): Promise<{ success: boolean; website: string; page_builder?: string; seo_plugin?: string; theme?: string }> {
    const response = await this.client.post('/builder-detection/confirm', {
      website,
      ...config,
    });
    return response.data;
  }

  async extractAdapterConfig(
    website: string
  ): Promise<{ success: boolean; website: string; builder_adapter_config: Record<string, unknown> }> {
    const response = await this.client.post('/builder-detection/extract-adapter', { website });
    return response.data;
  }

  async activateAdapter(
    website: string,
    status: 'active' | 'disabled',
    metaKeysOverride?: Record<string, string>
  ): Promise<{ success: boolean; website: string; status: string; builder_adapter_config: Record<string, unknown> }> {
    const response = await this.client.post('/builder-detection/activate-adapter', {
      website,
      status,
      meta_keys_override: metaKeysOverride,
    });
    return response.data;
  }

  async installHelperPlugin(
    website: string
  ): Promise<{ success: boolean; website: string; message: string; installed: boolean; activated: boolean; manual_install_required: boolean }> {
    const response = await this.client.post('/builder-detection/install-helper-plugin', { website });
    return response.data;
  }

  async seedAdapterConfig(
    website: string,
    builderName: string,
    metaKeys: Record<string, string>,
    shortcodeWrapper?: Record<string, unknown> | null
  ): Promise<{ success: boolean; website: string; builder_adapter_config: Record<string, unknown> }> {
    const response = await this.client.post('/builder-detection/seed-adapter', {
      website,
      builder_name: builderName,
      meta_keys: metaKeys,
      shortcode_wrapper: shortcodeWrapper ?? null,
    });
    return response.data;
  }

  // ====================================================
  // CONTENT CALENDAR ENDPOINTS
  // ====================================================

  // Brand Bible endpoints
  async getBrandBible(tenantId: string): Promise<BrandBible> {
    const response = await this.client.get<BrandBible>('/content-calendar/brand-bible', {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  async updateBrandBible(tenantId: string, bible: Omit<BrandBible, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<BrandBible> {
    const response = await this.client.put<BrandBible>('/content-calendar/brand-bible', bible, {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  async deleteBrandBible(tenantId: string): Promise<void> {
    await this.client.delete('/content-calendar/brand-bible', {
      params: { tenant_id: tenantId },
    });
  }

  // Keyword endpoints
  async getKeywords(
    tenantId: string,
    filters?: ContentCalendarFilters,
    sortBy = 'volume_monthly',
    sortDesc = true,
    page = 1,
    pageSize = 100
  ): Promise<{
    keywords: ContentCalendarKeyword[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const params: any = {
      tenant_id: tenantId,
      sort_by: sortBy,
      sort_desc: sortDesc,
      page,
      page_size: pageSize,
    };

    if (filters) {
      if (filters.status) params.status_filter = filters.status;
      if (filters.source) params.source_filter = filters.source;
      if (filters.min_volume !== undefined) params.min_volume = filters.min_volume;
      if (filters.max_volume !== undefined) params.max_volume = filters.max_volume;
      if (filters.min_difficulty !== undefined) params.min_difficulty = filters.min_difficulty;
      if (filters.max_difficulty !== undefined) params.max_difficulty = filters.max_difficulty;
      if (filters.search) params.search = filters.search;
    }

    const response = await this.client.get('/content-calendar/keywords', { params });
    return response.data;
  }

  async getKeyword(keywordId: string, tenantId: string): Promise<ContentCalendarKeyword> {
    const response = await this.client.get<ContentCalendarKeyword>(`/content-calendar/keywords/${keywordId}`, {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  async updateKeywordStatus(
    keywordId: string,
    tenantId: string,
    status: KeywordStatus,
    blogPostId?: string
  ): Promise<void> {
    await this.client.put(`/content-calendar/keywords/${keywordId}/status`, {
      status,
      blog_post_id: blogPostId,
    }, {
      params: { tenant_id: tenantId },
    });
  }

  async bulkUpdateKeywordStatus(keywordIds: string[], tenantId: string, status: KeywordStatus): Promise<number> {
    const response = await this.client.post<{ count: number }>(
      '/content-calendar/keywords/bulk-update-status',
      { keyword_ids: keywordIds, status },
      { params: { tenant_id: tenantId } }
    );
    return response.data.count;
  }

  async deleteKeyword(keywordId: string, tenantId: string): Promise<void> {
    await this.client.delete(`/content-calendar/keywords/${keywordId}`, {
      params: { tenant_id: tenantId },
    });
  }

  // Generation endpoints
  async generateContentCalendar(tenantId: string, config?: Record<string, any>): Promise<GenerationTask> {
    const response = await this.client.post<GenerationTask>('/content-calendar/generate', {
      tenant_id: tenantId,
      config,
    });
    return response.data;
  }

  async getGenerationTask(taskId: string, tenantId: string): Promise<GenerationTask> {
    const response = await this.client.get<GenerationTask>(`/content-calendar/tasks/${taskId}`, {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  async getGenerationTasks(tenantId: string, limit = 10): Promise<GenerationTask[]> {
    const response = await this.client.get<GenerationTask[]>('/content-calendar/tasks', {
      params: { tenant_id: tenantId, limit },
    });
    return response.data;
  }

  // Analytics endpoints
  async getContentCalendarAnalytics(tenantId: string): Promise<ContentCalendarAnalytics> {
    const response = await this.client.get<ContentCalendarAnalytics>('/content-calendar/analytics', {
      params: { tenant_id: tenantId },
    });
    return response.data;
  }

  // Convenience wrappers for hooks
  async getContentCalendarKeywords(
    tenantId: string,
    filters?: ContentCalendarFilters,
    sortBy = 'volume_monthly',
    sortDesc = true,
    page = 1,
    pageSize = 50
  ) {
    return this.getKeywords(tenantId, filters, sortBy, sortDesc, page, pageSize);
  }

  async getRecentGenerationTasks(tenantId: string, limit = 5) {
    return this.getGenerationTasks(tenantId, limit);
  }

  // Settings endpoints
  async testAhrefsConnection(apiToken: string): Promise<{ success: boolean; message: string; details?: any }> {
    const response = await this.client.post<{ success: boolean; message: string; details?: any }>(
      '/content-calendar/test-ahrefs-connection',
      { api_token: apiToken }
    );
    return response.data;
  }

  // ====================================================
  // LOCATION PAGE WIZARD ENDPOINTS
  // ====================================================
  async getTemplates(): Promise<TemplateConfig[]> {
    const response = await this.client.get<TemplateConfig[]>('/api/templates');
    return response.data;
  }

  async startGeneration(config: SeoContentConfig): Promise<{ crew_run_id: string, page_id: string, estimated_time_sec: number }> {
    const response = await this.client.post('/api/location-pages/start-generation', config);
    return response.data;
  }

  async getLocationPage(pageId: string): Promise<ContentReviewState> {
    const response = await this.client.get(`/api/location-pages/${pageId}`);
    return response.data;
  }

  async updateSection(pageId: string, sectionId: string, payload: { content: string, action: "save" | "regenerate" }): Promise<ContentReviewState> {
    const response = await this.client.patch(`/api/location-pages/${pageId}/sections/${sectionId}`, payload);
    return response.data;
  }

  async publishLocationPage(pageId: string, publishConfig: PublishRequest): Promise<{ wordpress_post_id: string, wordpress_post_url: string, status: string }> {
    const response = await this.client.post(`/api/location-pages/${pageId}/publish`, publishConfig);
    return response.data;
  }


  // ====================================================
  // LOCATION PAGES ENDPOINTS
  // ====================================================

  async locationPageKeywordResearch(keyword: string, country = 'us', language = 'en'): Promise<{
    keyword: string;
    keyword_difficulty: number | null;
    difficulty_level: string | null;
    search_volume: number | null;
    clicks: number | null;
  }> {
    const response = await this.client.post('/location-pages/keyword-research', {
      keyword,
      country,
      language,
    });
    return response.data;
  }

  async locationPageKeywordSuggestions(keyword: string, country = 'us', limit = 10): Promise<{
    seed_keyword: string;
    suggestions: Array<{
      keyword: string;
      keyword_difficulty: number | null;
      difficulty_level: string | null;
      search_volume: number | null;
      clicks: number | null;
    }>;
  }> {
    const response = await this.client.post('/location-pages/keyword-suggestions', {
      keyword,
      country,
      limit,
    });
    return response.data;
  }

  async locationPageAIKeywordSuggestion(params: {
    business_name: string;
    location: string;
    services_offered: string[];
    description?: string;
  }): Promise<{
    suggested_keyword: string;
    reasoning: string | null;
  }> {
    const response = await this.client.post('/location-pages/ai-keyword-suggestion', params);
    return response.data;
  }

  async launchLocationPageBatch(request: {
    jobs: Array<{
      location_name: string;
      service_name: string;
      keywords: string[];
      slug: string;
      tone: string;
      config?: {
        contentRequirements?: {
          wordCountMin?: number;
          wordCountMax?: number;
          keywordDensityMin?: number;
          keywordDensityMax?: number;
          externalLinks?: number;
          internalLinksPer300Words?: number;
          metaDescriptionMin?: number;
          metaDescriptionMax?: number;
          keywordInFirstParagraph?: boolean;
          keywordInHeadings?: boolean;
          useBulletPoints?: boolean;
        };
        conversionElements?: {
          includeTestimonials?: boolean;
          includeMap?: boolean;
          includeStickyCTA?: boolean;
          includeFAQ?: boolean;
        };
        imageSettings?: {
          count?: number;
          orientation?: string;
          model?: string;
          style?: string;
        };
      };
    }>;
    brand_bible: Record<string, any>;
    website: string;
  }): Promise<{
    tasks: Array<{ task_id: string; status: string; message: string; created_at: string }>;
    total: number;
    message: string;
  }> {
    const response = await this.client.post('/location-pages/batch', request);
    return response.data;
  }

  async getLocationPageTaskStatus(taskId: string): Promise<{
    task_id: string;
    status: string;
    created_at: string;
    started_at: string | null;
    completed_at: string | null;
    result: any | null;
    error: string | null;
    progress: string | null;
  }> {
    const response = await this.client.get(`/location-pages/${taskId}`);
    return response.data;
  }

  async listLocationPageTasks(limit = 20): Promise<{
    tasks: any[];
    count: number;
    limit: number;
  }> {
    const response = await this.client.get('/location-pages/', { params: { limit } });
    return response.data;
  }

  // Enhanced Location Flow
  async runEnhancedLocationFlow(config: any): Promise<any> {
    const response = await this.client.post('/location-pages/enhanced-flow', config);
    return response.data;
  }

  // ====================================================
  // CONTENT OPTIMIZATION ENDPOINTS
  // ====================================================

  async analyzeContentOptimization(request: ContentOptimizationRequest): Promise<ContentOptimizationResponse> {
    const response = await this.client.post<ContentOptimizationResponse>('/content-optimization/analyze', request);
    return response.data;
  }

  async getOptimizationAnalysis(analysisId: string): Promise<OptimizationStatusResponse> {
    const response = await this.client.get<OptimizationStatusResponse>(`/content-optimization/analyses/${analysisId}`);
    return response.data;
  }

  async applyOptimizations(request: ApplyOptimizationsRequest): Promise<ApplyOptimizationsResponse> {
    const response = await this.client.post<ApplyOptimizationsResponse>('/content-optimization/apply', request);
    return response.data;
  }

  // ====================================================
  // SERVICE IMAGE ENDPOINTS
  // ====================================================

  async listServicesWithImages(tenantId: string): Promise<ServiceImageListItem[]> {
    const response = await this.client.get<ServiceImageListItem[]>(`/service-images/tenant/${tenantId}`);
    return response.data;
  }

  async getServiceImages(tenantId: string, serviceName: string): Promise<ServiceImagesGetResponse> {
    const response = await this.client.get<ServiceImagesGetResponse>(`/service-images/${tenantId}/${encodeURIComponent(serviceName)}`);
    return response.data;
  }

  async saveServiceImages(
    tenantId: string,
    serviceName: string,
    images: ServiceImage[],
    imagePrompts?: string[]
  ): Promise<{ success: boolean; message: string; service_name: string; tenant_id: string }> {
    const response = await this.client.post('/service-images/', {
      tenant_id: tenantId,
      service_name: serviceName,
      images,
      image_prompts: imagePrompts,
    });
    return response.data;
  }

  async deleteServiceImages(tenantId: string, serviceName: string): Promise<ServiceImagesDeleteResponse> {
    const response = await this.client.delete<ServiceImagesDeleteResponse>(`/service-images/${tenantId}/${encodeURIComponent(serviceName)}`);
    return response.data;
  }

  async getServiceImageDetails(tenantId: string, serviceName: string): Promise<ServiceImageDetail[]> {
    const response = await this.client.get<ServiceImageDetail[]>(`/service-images/${tenantId}/${encodeURIComponent(serviceName)}/details`);
    return response.data;
  }

  // ====================================================
  // SERVICE PAGE CREATION ENDPOINTS
  // ====================================================

  async createServicePage(request: ServicePageCreationRequest): Promise<ServicePageCreationResponse> {
    const response = await this.client.post<ServicePageCreationResponse>('/service-page/create', request);
    return response.data;
  }

  async getServicePageHealth(): Promise<{ status: string; service: string }> {
    const response = await this.client.get('/service-page/health');
    return response.data;
  }

  // ====================================================
  // INTEGRATION ENDPOINTS
  // ====================================================
  async testIntegration(service: string, settings: Record<string, any>): Promise<{ success: boolean; message: string; details?: any }> {
    const response = await this.client.post<{ success: boolean; message: string; details?: any }>('/integrations/test', {
      service,
      settings,
    });
    return response.data;
  }

  async checkWpConnectionStatus(tenantId: string): Promise<{ wp_connected: boolean }> {
    const response = await this.client.get<{ wp_connected: boolean }>(`/api/plugin/connection-status?tenant_id=${encodeURIComponent(tenantId)}`);
    return response.data;
  }

  async saveWordPressKey(secretKey: string, tenantId: string): Promise<{ success: boolean; wp_connected: boolean; verified?: boolean; tenant_id: string }> {
    const response = await this.client.post<{ success: boolean; wp_connected: boolean; verified?: boolean; tenant_id: string }>('/api/plugin/save-key', {
      secret_key: secretKey,
      tenant_id: tenantId,
    });
    return response.data;
  }

  async disconnectWordPress(tenantId: string): Promise<{ disconnected: boolean; tenant_id: string }> {
    const response = await this.client.post<{ disconnected: boolean; tenant_id: string }>('/api/plugin/disconnect', {
      tenant_id: tenantId,
    });
    return response.data;
  }

  /**
   * Open SSE connection to wait for plugin verification.
   * Returns an EventSource that will receive 'verified' or 'timeout' events.
   */
  waitForPluginVerification(secretKey: string, tenantId: string): EventSource {
    const url = `${this.baseURL}/api/plugin/wait-verification?secret_key=${encodeURIComponent(secretKey)}&tenant_id=${encodeURIComponent(tenantId)}`;
    return new EventSource(url);
  }

  // ====================================================
  // AI IMAGE GENERATION ENDPOINTS
  // ====================================================

  /**
   * Generate an AI image using fal.ai
   */
  async generateAIImage(request: {
    prompt: string;
    artStyle?: 'illustration' | 'photorealistic' | 'abstract' | '3d' | 'line-art' | 'custom';
    aspectRatio?: '1:1' | '3:4' | '4:3' | '16:9' | '9:16';
    model?: string;
    useThemeStyle?: boolean;
    themeStylePrompt?: string;
    count?: number;
  }): Promise<{
    images: Array<{
      url: string;
      alt_text: string;
      prompt_used: string;
    }>;
    model_used: string;
  }> {
    console.log('[IMAGE_TRACE] [FRONTEND:SERVICE] AI_GEN_REQUEST - API call to generate AI image', {
      prompt: request.prompt.length > 100 ? `${request.prompt.substring(0, 100)}...` : request.prompt,
      artStyle: request.artStyle,
      aspectRatio: request.aspectRatio,
      model: request.model,
      useThemeStyle: request.useThemeStyle,
    });

    // Map art style to style prompt enhancement
    const styleMap: Record<string, string> = {
      'illustration': 'illustration style, artistic, hand-drawn aesthetic',
      'photorealistic': 'photorealistic, high quality photograph, professional photography',
      'abstract': 'abstract art style, creative, artistic interpretation',
      '3d': '3D rendered, volumetric lighting, cinema 4d style',
      'line-art': 'line art style, minimalist, clean lines, sketch',
      'custom': '',
    };

    const styleEnhancement = styleMap[request.artStyle || 'photorealistic'] || '';
    const enhancedPrompt = request.useThemeStyle && request.themeStylePrompt
      ? `${request.prompt}. ${styleEnhancement}. ${request.themeStylePrompt}`
      : `${request.prompt}. ${styleEnhancement}`;

    try {
      const response = await this.client.post('/blog-creation/content/generate-hero-image', {
        title: request.prompt,
        keyword: request.prompt.split(' ').slice(0, 3).join(' '),
        style: request.artStyle || 'modern',
      });

      console.log('[IMAGE_TRACE] [FRONTEND:SERVICE] AI_GEN_RESPONSE - API response received', {
        success: true,
        imageUrl: response.data.image_url?.length > 100 ? `${response.data.image_url.substring(0, 100)}...` : response.data.image_url,
        altText: response.data.alt_text,
      });

      return {
        images: [{
          url: response.data.image_url,
          alt_text: response.data.alt_text,
          prompt_used: response.data.prompt_used,
        }],
        model_used: request.model || DEFAULT_IMAGE_MODEL,
      };
    } catch (error) {
      console.log('[IMAGE_TRACE] [FRONTEND:SERVICE] AI_GEN_RESPONSE - API call failed', {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Enhance an image prompt using AI
   */
  async enhanceImagePrompt(prompt: string, context?: string): Promise<{
    enhanced_prompt: string;
    suggestions: string[];
  }> {
    // For now, return a simple enhancement - can be connected to backend later
    const enhancements = [
      'high quality',
      'professional lighting',
      'detailed',
      '4k resolution',
    ];

    const enhancedPrompt = `${prompt}, ${enhancements.join(', ')}`;

    return {
      enhanced_prompt: enhancedPrompt,
      suggestions: enhancements,
    };
  }
}

export const apiService = new ApiService();

// Export the raw axios client for services that need direct access
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

export default apiService;
